'use client';

/**
 * Getting Started progress as Intercom user attributes (VALE-1884).
 *
 * **Why attributes, when GUIDE-3 already sends a completion event.** An Intercom
 * checklist step ticks itself on a product event, a user attribute, an event
 * attribute or a tour completion. `guidance-getting-started-step-completed`
 * (VALE-1790) is the event, and it undercounts by design: the browser reports a
 * step only when it sees the step change during the session. A step finished in
 * another session, on another device or by the server alone never reaches
 * Intercom, so a checklist bound to the event would leave the row unticked for
 * exactly the user who did the work somewhere else. These attributes carry the
 * server's own answer (`GET /onboarding/status`, the record VALE-1830 made
 * authoritative), so a checklist bound to them agrees with the in-app checklist
 * row every time the app is open.
 *
 * **Every step is covered by construction.** Membership is derived from
 * `CANONICAL_STEPS`, so a seventh step gets an attribute without anyone
 * remembering to add one; the test reconciles the derived names against the
 * step list.
 *
 * **The names are published.** A checklist rule, a segment or a tour audience
 * pointed at one of them breaks silently if it is renamed, exactly as GUIDE-1's
 * boot attributes and GUIDE-3's event names do. Treat them as fixed.
 */

import { update } from '@intercom/messenger-js-sdk';
import { CANONICAL_STEPS } from '@/lib/getting-started/steps';
import type {
  GettingStartedStatus,
  GettingStartedStepKey
} from '@/lib/services/onboarding/types';

/** The prefix every attribute here shares, so one Intercom filter lists the family. */
export const INTERCOM_GETTING_STARTED_ATTRIBUTE_PREFIX = 'getting_started_';

/** The attribute carrying how many of the steps are done. */
export const INTERCOM_GETTING_STARTED_COMPLETED_COUNT =
  `${INTERCOM_GETTING_STARTED_ATTRIBUTE_PREFIX}completed_count` as const;

/**
 * The attribute name for one step: `notify-institutions` becomes
 * `getting_started_notify_institutions`. Snake case because every custom
 * attribute already in the workspace is, and Intercom shows the raw name in its
 * rule builder.
 */
export function intercomGettingStartedAttributeName(
  step: GettingStartedStepKey
): string {
  return `${INTERCOM_GETTING_STARTED_ATTRIBUTE_PREFIX}${step.replace(/-/g, '_')}`;
}

export type IntercomGettingStartedAttributes = Record<
  string,
  boolean | number | undefined
>;

/**
 * One boolean per canonical step, plus the completed count.
 *
 * A step the response does not carry is left `undefined` (unset on the contact)
 * rather than `false`: the server always returns all six, so absence means the
 * two packages have drifted, and writing "not done" would put a confident wrong
 * answer in front of a checklist rule.
 */
export function buildIntercomGettingStartedAttributes(
  status: GettingStartedStatus
): IntercomGettingStartedAttributes {
  const byId = new Map(status.steps.map((step) => [step.id, step.completed]));

  const attributes: IntercomGettingStartedAttributes = {};
  for (const step of CANONICAL_STEPS) {
    attributes[intercomGettingStartedAttributeName(step)] = byId.get(step);
  }
  attributes[INTERCOM_GETTING_STARTED_COMPLETED_COUNT] = status.completedCount;
  return attributes;
}

/**
 * What was last sent, and for whom, so an unchanged snapshot sends nothing.
 *
 * Module-level for the same reason as GUIDE-3's completion observer: the
 * progress hook is read by three surfaces at once (the rail is on every page),
 * and per-instance state would send the same update three times. The user id is
 * part of the key so a sign-in as someone else in the same tab still sends.
 */
let lastSent: string | null = null;

/**
 * Push the current progress to the Messenger's contact, when it has changed.
 *
 * The caller gates on the Messenger being booted: an `update` before boot is
 * dropped by Intercom, and recording it as sent would suppress the retry once
 * the Messenger is up. Fire-and-forget and never throws, for the reason every
 * guidance call is: none of this is worth more than what the user was doing.
 */
export function syncGettingStartedAttributesToIntercom(
  userId: number | string,
  status: GettingStartedStatus
): void {
  const attributes = buildIntercomGettingStartedAttributes(status);
  const key = JSON.stringify([String(userId), attributes]);
  if (key === lastSent) return;

  try {
    update(attributes);
    lastSent = key;
  } catch {
    // Leave `lastSent` alone so the next snapshot tries again.
  }
}

/** Clears the dedupe state. **Tests only.** */
export function resetGettingStartedAttributeSyncForTest(): void {
  lastSent = null;
}
